import React, { useState, useEffect } from 'react';
import { X, UserCircle, MessageCircle, FolderOpen } from 'lucide-react';
import ClientInfoTab from './tabs/ClientInfoTab';
import CommentTab from './tabs/CommentTab';
import DocumentTab from './tabs/DocumentTab';
import type { FormSubmission, ClientDetails, Comment } from '../../types';
import { supabase } from '../../lib/supabaseClient';

interface ClientDetailsPanelProps {
  client: FormSubmission;
  clientDetails: ClientDetails;
  comments: Comment | null;
  onClose: () => void;
  onRefreshClientDetails: () => void;
  onDeleteFile: (fileId: string, filePath: string) => Promise<void>;
}

type TabType = 'client' | 'comments' | 'documents';

export default function ClientDetailsPanel({
  client,
  clientDetails,
  comments,
  onClose,
  onRefreshClientDetails,
  onDeleteFile
}: ClientDetailsPanelProps) {
  const [activeTab, setActiveTab] = useState<TabType>('client');

  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone}`;
  };

  const handleEmail = (email: string) => {
    window.location.href = `mailto:${email}`;
  };

  // Mark as viewed when opened
  useEffect(() => {
    const markAsViewed = async () => {
      if (!client.viewed) {
        await supabase
          .from('form_submissions')
          .update({ viewed: true })
          .eq('id', client.id);
      }
    };
    
    markAsViewed();
  }, [client.id, client.viewed]);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full" style={{ zIndex: 9999 }}>
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-lg bg-white">
        <div className="flex justify-between items-center mb-6">
          <div className="flex flex-col">
            <h2 className="text-2xl font-bold text-gray-900">Fiche Prospect - Client</h2>
            <p className="text-sm text-gray-500 mt-1">
              Référence: {client.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('client')}
              className={`${
                activeTab === 'client'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <UserCircle className="h-5 w-5 mr-2" />
              Client
            </button>

            <button
              onClick={() => setActiveTab('comments')}
              className={`${
                activeTab === 'comments'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Commentaires
            </button>

            <button
              onClick={() => setActiveTab('documents')}
              className={`${
                activeTab === 'documents'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <FolderOpen className="h-5 w-5 mr-2" />
              Documents
            </button>
          </nav>
        </div>

        <div className="mt-6">
          {activeTab === 'client' && (
            <ClientInfoTab
              client={client}
              onCall={handleCall}
              onEmail={handleEmail}
            />
          )}

          {activeTab === 'comments' && (
            <CommentTab
              submissionId={client.id}
              comments={comments}
              onRefresh={onRefreshClientDetails}
            />
          )}

          {activeTab === 'documents' && (
            <DocumentTab
              submissionId={client.id}
              files={clientDetails.files}
              onRefresh={onRefreshClientDetails}
              onDeleteFile={onDeleteFile}
            />
          )}
        </div>
      </div>
    </div>
  );
}